<div>
    <!-- Unified Top Navigation Bar -->
    <nav class="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="flex h-16 items-center justify-between">
                <!-- Left: Logo + Desktop Navigation -->
                <div class="flex items-center gap-x-8">
                    <!-- Logo -->
                    <a routerLink="/dashboard" class="flex items-center gap-x-2">
                        <img class="h-8 w-auto rounded-lg" src="./logo-only.webp" alt="Hive Mind Logo" />
                        <span class="hidden md:block text-base font-semibold"> Hive <span class="text-blue-400">Mind</span> </span>
                        <span class="hidden md:block text-xs text-gray-400">v{{ version }}</span>
                    </a>

                    <!-- Desktop Navigation (text-only) -->
                    <div class="hidden lg:flex lg:gap-x-6 lg:h-full lg:items-stretch">
                        @for (item of navItems; track item) {
                            <a
                                [routerLink]="item.route"
                                [routerLinkActiveOptions]="{ exact: item.exact }"
                                routerLinkActive="text-blue-400 border-b-2 border-blue-400"
                                class="flex items-center text-sm font-semibold text-gray-700 hover:text-blue-400 border-b-2 border-transparent transition-colors"
                            >
                                {{ item.label }}
                            </a>
                        }
                    </div>
                </div>

                <!-- Right: Mobile Menu Button + User Menu -->
                <div class="flex items-center gap-x-4">
                    <!-- Mobile menu button -->
                    <button
                        type="button"
                        class="lg:hidden -m-2.5 p-2.5 text-gray-700"
                        (click)="toggleMobileMenu()"
                        [attr.aria-expanded]="showSidebarMenu"
                        aria-label="Toggle navigation menu"
                    >
                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                        </svg>
                    </button>

                    <!-- User menu -->
                    <div class="relative">
                        <button
                            type="button"
                            (click)="toggleMenu()"
                            [attr.aria-expanded]="showUserMenu"
                            aria-label="User menu"
                            class="flex items-center gap-x-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
                        >
                            @if (user?.profilePictureUrl) {
                                <img
                                    [src]="$safeNavigationMigration(user?.profilePictureUrl)"
                                    class="h-8 w-8 rounded-full"
                                    [alt]="$safeNavigationMigration(user?.name)"
                                />
                            }
                            @if (!user?.profilePictureUrl) {
                                <svg class="h-8 w-8 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        fill-rule="evenodd"
                                        d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            }
                            <span class="hidden md:block">{{ user?.name || 'User' }}</span>
                            <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fill-rule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>

                        <!-- User dropdown -->
                        @if (showUserMenu) {
                            <div
                                @fadeInOut
                                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5"
                            >
                                <a
                                    routerLink="/user/profile"
                                    (click)="toggleMenu()"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Your Profile
                                </a>
                                <button
                                    (click)="auth.logout({ logoutParams: { returnTo: document.location.origin } })"
                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Sign out
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    </nav>

    <!-- Mobile Menu Overlay (slides from left) -->
    @if (showSidebarMenu) {
        <div class="lg:hidden" role="dialog" aria-modal="true">
            <!-- Backdrop -->
            <div class="fixed inset-0 z-40 bg-gray-900/80" @backdropFade (click)="toggleMobileMenu()"></div>
            <!-- Menu panel -->
            <div class="fixed inset-y-0 left-0 z-50 w-full max-w-xs bg-white" @slideInOut>
                <div class="flex h-16 items-center justify-between px-6 border-b border-gray-200">
                    <span class="text-base font-semibold">Menu</span>
                    <button type="button" (click)="toggleMobileMenu()" class="-m-2.5 p-2.5 text-gray-700">
                        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <nav class="px-6 py-4" aria-label="Mobile navigation">
                    <ul role="list" class="space-y-2">
                        @for (item of navItems; track item) {
                            <li>
                                <a
                                    [routerLink]="item.route"
                                    [routerLinkActiveOptions]="{ exact: item.exact }"
                                    routerLinkActive="bg-gray-50 text-blue-400"
                                    (click)="toggleMobileMenu()"
                                    class="flex items-center gap-x-3 p-3 text-sm font-semibold text-gray-700 rounded-md hover:bg-gray-50 hover:text-blue-400"
                                >
                                    <span class="h-6 w-6" [innerHTML]="item.icon | safeHtml" aria-hidden="true"></span>
                                    {{ item.label }}
                                </a>
                            </li>
                        }
                    </ul>
                </nav>
            </div>
        </div>
    }

    <!-- Main Content (full width, no sidebar offset) -->
    <main class="bg-gray-100 min-h-screen">
        <app-loading></app-loading>

        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <router-outlet />
        </div>
    </main>
</div>
